import * as React from 'react';
import './NavBar.css';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/Info';
import MenuIcon from '@mui/icons-material/Menu';
import ArchiveIcon from '@mui/icons-material/Archive';
import FeedbackIcon from '@mui/icons-material/Feedback';
import {
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { CONSTANTS } from '../../constants/constants';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 14,
  },
}));

export default function NavBar(props: Props) {
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleArchive = () => {
    handleClose();
    navigate('/archive');
  };

  const handleInfo = () => {
    handleClose();
    props.infoButtonOnClick();
  };

  return (
    <div className="nav-header">
      <AppBar position="static" color="primary">
        <Toolbar
          disableGutters
          sx={{
            padding: '0 12px',
          }}
        >
          <Box flexGrow={1} display="flex" justifyContent="flex-start">
            <IconButton
              id="menu-button"
              aria-controls={open ? 'positioned-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="positioned-menu"
              aria-labelledby="demo-positioned-button"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom', // Aligns the menu vertically below the icon
                horizontal: 'left', // Keeps it aligned to the left edge of the icon
              }}
              transformOrigin={{
                vertical: 'top', // Menu appears growing downward
                horizontal: 'left',
              }}
              PaperProps={{
                style: {
                  width: 200,
                },
              }}
            >
              <MenuItem onClick={handleArchive}>
                <ListItemIcon>
                  <ArchiveIcon fontSize="small" htmlColor="black" />
                </ListItemIcon>
                <ListItemText>Archive</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleInfo}>
                <ListItemIcon>
                  <InfoIcon fontSize="small" htmlColor="black" />
                </ListItemIcon>
                <ListItemText>About</ListItemText>
              </MenuItem>
              <MenuItem component="a" href={CONSTANTS.FEEDBACK_LINK}>
                <ListItemIcon>
                  <FeedbackIcon fontSize="small" htmlColor="black" />
                </ListItemIcon>
                <ListItemText>Feedback</ListItemText>
              </MenuItem>
            </Menu>
          </Box>

          {/* Center — Title */}
          <Typography
            variant="h3"
            component="h1"
            fontWeight="bold"
            sx={{
              flexGrow: 1,
              textAlign: 'center',
            }}
          >
            <a href="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              WORMLE
            </a>
          </Typography>

          {/* Right side — Info + Help */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
            flexGrow={1}
          >
            <LightTooltip title="Help">
              <IconButton
                onClick={props.helpButtonOnClick}
                size="large"
                color="inherit"
                aria-label="help"
              >
                <HelpIcon />
              </IconButton>
            </LightTooltip>
          </Box>
        </Toolbar>
      </AppBar>
    </div>
  );
}

interface Props {
  infoButtonOnClick: () => void;
  helpButtonOnClick: () => void;
}
